import type { AgentDevice } from '@staff-app/shared-types';
import type { LiveDeviceStatus } from '../../mdm-devices/hooks/useDeviceHeartbeats';
import type { AgentDotStatus } from '../components/AgentDeviceStatusDot';

export type AgentDeviceStatus = 'online' | 'stale' | 'offline';

/**
 * Classify a device's online status from its last heartbeat. Mirrors the
 * MDM device view thresholds (features/mdm-devices/DeviceStatusDot):
 * - fresh (≤ 13 min) → online
 * - ≤ 24h → stale
 * - missing / > 24h → offline
 *
 * A removed device (found=false, no heartbeat) classifies as offline.
 */
export function classifyAgentDeviceStatus(lastHeartBeat?: string | null): AgentDeviceStatus {
  const beat = lastHeartBeat ? new Date(lastHeartBeat).getTime() : 0;
  if (!beat) return 'offline';
  const ageMs = Date.now() - beat;
  if (ageMs / 60000 <= 13) return 'online';
  if (ageMs <= 24 * 60 * 60 * 1000) return 'stale';
  return 'offline';
}

/**
 * Effective status for a device: the live heartbeat status (online/hdmiOff/
 * offline) when present, else the lastHeartBeat-based classification (which can
 * also yield `stale`). Used by both the row dot and the summary rollup so they
 * always agree.
 */
export function effectiveAgentDeviceStatus(
  device: AgentDevice,
  liveStatusByUuid?: Map<string, LiveDeviceStatus>,
): AgentDotStatus {
  const uuid = device.device?.UUID;
  const live = uuid ? liveStatusByUuid?.get(uuid) : undefined;
  return live ?? classifyAgentDeviceStatus(device.device?.lastHeartBeat);
}

export interface AgentDeviceStatusRollup {
  total: number;
  online: number;
  stale: number;
  /** Online-but-HDMI-disconnected (live heartbeat type 2) — distinct from stale. */
  hdmiOff: number;
  offline: number;
}

/**
 * Roll up device statuses for the summary cards. `hdmiOff` (online but HDMI
 * disconnected) is counted separately from `stale` (an old/missing heartbeat)
 * so the two attention states aren't conflated — both render amber, but they
 * mean different things to an operator.
 */
export function summarizeAgentDeviceStatuses(
  devices: AgentDevice[],
  liveStatusByUuid?: Map<string, LiveDeviceStatus>,
): AgentDeviceStatusRollup {
  const rollup: AgentDeviceStatusRollup = {
    total: devices.length,
    online: 0,
    stale: 0,
    hdmiOff: 0,
    offline: 0,
  };
  for (const d of devices) {
    const status = effectiveAgentDeviceStatus(d, liveStatusByUuid);
    if (status === 'online') rollup.online++;
    else if (status === 'offline') rollup.offline++;
    else if (status === 'hdmiOff') rollup.hdmiOff++;
    else rollup.stale++;
  }
  return rollup;
}
